import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Coins, Trophy, Filter, Minus, Plus, History } from "lucide-react";
import { Navbar, Footer } from "@/components/Chrome";
import { useAuth, startLogin } from "@/context/AuthContext";
import { api, money, money2, num, REDEMPTION_STATUS } from "@/lib/spott";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Rewards() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [f, setF] = useState({ city: "", stage: "" });
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ units: 1, months: 1, note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Puntos Spott — Canjea tu inversión";
    api.get("/rewards").then(({ data }) => setData(data));
  }, []);

  const loadMine = () => {
    api.get("/loyalty/me").then(({ data }) => setLoyalty(data)).catch(() => {});
    api.get("/redemptions/mine").then(({ data }) => setRedemptions(data)).catch(() => {});
  };

  useEffect(() => { if (user) loadMine(); }, [user]);

  const submitRedemption = async () => {
    setBusy(true);
    try {
      await api.post("/redemptions", {
        space_id: modal.space_id, units: form.units, months: form.months, note: form.note,
      });
      toast.success("¡Canje solicitado! Un agente Spott lo confirmará en breve.");
      setModal(null);
      setForm({ units: 1, months: 1, note: "" });
      loadMine();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el canje");
    } finally { setBusy(false); }
  };

  const cities = useMemo(() => [...new Set((data?.items || []).map((i) => i.city))].sort(), [data]);
  const items = useMemo(
    () => (data?.items || []).filter((i) =>
      (!f.city || i.city === f.city) && (!f.stage || i.funnel_stage === f.stage)),
    [data, f]
  );
  const balance = loyalty?.points_balance || 0;

  return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />
      <div className="relative overflow-hidden bg-spott-blue py-16 grain" data-testid="rewards-hero">
        <div className="arc-deco -right-24 -top-28 h-80 w-80" />
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">
            Programa de fidelización
          </p>
          <h1 className="mt-4 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Cada dólar ejecutado se convierte en puntos Spott
          </h1>
          <p className="mt-5 max-w-2xl text-sm font-light text-spott-mid md:text-base">
            Ganas <strong className="text-spott-yellow">{data?.points_per_dollar || 1} punto por cada dólar</strong>{" "}
            de campaña ejecutada, más un <strong className="text-spott-yellow">bono según tu nivel</strong>{" "}
            (Plata +5%, Oro +10%, Platino +15%), y los canjeas por espacios publicitarios en toda la red de
            retailers. Equivalencia de canje: {num(data?.points_per_dollar_redeem || 20)} puntos = $1 de valor
            del espacio.
          </p>
          {user ? (
            <div className="mt-10 grid gap-5 sm:grid-cols-4" data-testid="loyalty-summary">
              {[
                ["Puntos disponibles", num(balance)],
                ["Bono por nivel", `+${num(loyalty?.points_bonus || 0)}`],
                ["Puntos canjeados", num(loyalty?.points_spent || 0)],
                ["Nivel actual", `${loyalty?.tier || "Bronce"} (+${loyalty?.tier_bonus_percent || 0}%)`],
              ].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-white/15 bg-white/5 p-5">
                  <div className="text-xs font-light text-spott-mid">{l}</div>
                  <div className="mt-1 text-2xl font-bold text-spott-yellow">{v}</div>
                </div>
              ))}
            </div>
          ) : (
            <button onClick={() => startLogin("/recompensas")} data-testid="rewards-login-btn"
              className="mt-9 rounded-full bg-spott-yellow px-7 py-4 text-sm font-bold text-spott-blue">
              Inicia sesión para ver tus puntos
            </button>
          )}
          {loyalty?.next_tier && (
            <p className="mt-5 text-sm font-light text-spott-mid" data-testid="loyalty-next-tier">
              Te faltan <strong className="text-spott-yellow">{num(loyalty.points_to_next)}</strong> puntos para
              alcanzar el nivel {loyalty.next_tier} y subir tu bono de acreditación.
            </p>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10">
        <h2 className="flex items-center gap-2 text-xl font-bold text-spott-blue sm:text-2xl">
          <Trophy size={20} className="text-spott-yellow" /> Niveles del programa
        </h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="tiers-grid">
          {(data?.tiers || []).map((t) => (
            <div key={t.tier} data-testid={`tier-${t.tier}`}
              className={`rounded-xl border p-6 ${
                loyalty?.tier === t.tier ? "border-spott-yellow bg-spott-yellow/10" : "border-spott-mid bg-white"
              }`}>
              <h3 className="text-base font-semibold text-spott-blue">{t.tier}</h3>
              <p className="mt-1 text-xs font-medium text-spott-yellow">
                desde {num(t.min_points)} pts · +{t.bonus_percent}% de puntos
              </p>
              <p className="mt-3 text-sm text-gray-600">{t.perk}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-end justify-between gap-4">
          <h2 className="flex items-center gap-2 text-xl font-bold text-spott-blue sm:text-2xl">
            <Gift size={20} className="text-spott-yellow" /> Catálogo de canje
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <Filter size={15} className="text-spott-yellow" />
            <select value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })}
              data-testid="rewards-filter-city"
              className="h-10 rounded-lg border border-spott-mid bg-white px-3 text-sm text-spott-blue">
              <option value="">Todas las ciudades</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}
              data-testid="rewards-filter-stage"
              className="h-10 rounded-lg border border-spott-mid bg-white px-3 text-sm text-spott-blue">
              <option value="">Awareness y conversión</option>
              <option value="awareness">Awareness</option>
              <option value="conversion">Conversión</option>
            </select>
          </div>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="rewards-grid">
          {items.map((i) => {
            const affordable = user && balance >= i.points_cost;
            return (
              <div key={i.space_id} data-testid={`reward-card-${i.space_id}`}
                className={`overflow-hidden rounded-xl border bg-white transition-transform hover:-translate-y-1 ${
                  affordable ? "border-spott-yellow" : "border-spott-mid"
                }`}>
                <div className="relative h-36 overflow-hidden bg-spott-light">
                  {i.image && <img src={i.image} alt={i.name} loading="lazy" className="h-full w-full object-cover" />}
                  <span className="absolute left-3 top-3 rounded-full bg-spott-blue/90 px-3 py-1 text-[11px] font-medium text-spott-yellow">
                    {i.funnel_stage === "awareness" ? "Awareness" : "Conversión"}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-sm font-semibold leading-snug text-spott-blue">{i.name}</h3>
                  <p className="mt-1 text-xs text-gray-500">{i.retailer_name} · {i.city} · {i.format}</p>
                  <div className="mt-4 flex items-end justify-between border-t border-spott-mid pt-4">
                    <div>
                      <div className="text-xs text-gray-500">Costo en puntos</div>
                      <div className="flex items-center gap-1.5 text-lg font-bold text-spott-blue"
                        data-testid={`reward-points-${i.space_id}`}>
                        <Coins size={15} className="text-spott-yellow" /> {num(i.points_cost)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Valor / mes</div>
                      <div className="text-sm font-semibold text-spott-blue">{money(i.price_month)}</div>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    {user
                      ? affordable
                        ? "Tienes puntos suficientes para canjearlo."
                        : `Te faltan ${num(i.points_cost - balance)} puntos`
                      : `${num(i.points_cost)} pts equivalen a ${money(i.price_month)} de inversión`}
                  </p>
                  {user ? (
                    <button onClick={() => { setModal(i); setForm({ units: 1, months: 1, note: "" }); }}
                      disabled={!affordable} data-testid={`redeem-btn-${i.space_id}`}
                      className="mt-4 w-full rounded-full bg-spott-yellow py-2.5 text-sm font-bold text-spott-blue transition-colors disabled:cursor-not-allowed disabled:bg-spott-mid disabled:text-gray-500">
                      {affordable ? "Canjear con puntos" : "Puntos insuficientes"}
                    </button>
                  ) : (
                    <button onClick={() => startLogin("/recompensas")} data-testid={`redeem-login-${i.space_id}`}
                      className="mt-4 w-full rounded-full border border-spott-mid py-2.5 text-sm font-bold text-spott-blue">
                      Inicia sesión para canjear
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-xl border border-spott-mid bg-white p-6 text-sm text-gray-600">
          Los puntos se acreditan cuando la campaña queda aprobada y ejecutada. Al solicitar un canje, los
          puntos quedan reservados y un agente Spott confirma la disponibilidad del espacio en la fecha
          solicitada.
          <Link to="/cotizar" className="ml-1 font-semibold text-spott-blue underline">Cotiza tu próxima campaña</Link>{" "}
          y empieza a acumular.
        </div>

        {user && loyalty?.ledger?.length > 0 && (
          <div className="mt-14" data-testid="points-ledger">
            <h2 className="flex items-center gap-2 text-xl font-bold text-spott-blue sm:text-2xl">
              <Coins size={20} className="text-spott-yellow" /> Cómo acumulaste tus puntos
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Cada campaña acreditada suma sus puntos base más el bono del nivel que tenías en ese momento.
              Inversión ejecutada total: {money2(loyalty.invested)}.
            </p>
            <div className="mt-5 overflow-hidden rounded-xl border border-spott-mid bg-white">
              {loyalty.ledger.map((l) => (
                <div key={l.quote_id} data-testid={`ledger-row-${l.quote_id}`}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-spott-mid px-6 py-4 text-sm">
                  <div>
                    <div className="font-semibold text-spott-blue">{l.company_name || "Campaña"}</div>
                    <div className="text-xs text-gray-500">{l.date} · {money2(l.amount)} ejecutados</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs">
                    <span className="text-gray-600">{num(l.base_points)} base</span>
                    <span className={`rounded-full px-3 py-1 font-semibold ${
                      l.bonus_points > 0 ? "bg-spott-yellow text-spott-blue" : "bg-spott-light text-gray-500"
                    }`}>
                      Nivel {l.tier} · bono +{l.bonus_percent}% = {num(l.bonus_points)} pts
                    </span>
                    <span className="text-base font-bold text-spott-blue">{num(l.total_points)} pts</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {user && (
          <div className="mt-12" data-testid="my-redemptions">
            <h2 className="flex items-center gap-2 text-xl font-bold text-spott-blue sm:text-2xl">
              <History size={20} className="text-spott-yellow" /> Mis canjes
            </h2>
            {redemptions.length === 0 ? (
              <p className="mt-4 rounded-xl border border-spott-mid bg-white p-6 text-sm text-gray-600">
                Todavía no has solicitado canjes. Elige un espacio del catálogo y úsalo con tus puntos.
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                {redemptions.map((r) => (
                  <div key={r.redemption_id} data-testid={`redemption-row-${r.redemption_id}`}
                    className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-spott-mid bg-white p-5">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${REDEMPTION_STATUS[r.status].cls}`}>
                          {REDEMPTION_STATUS[r.status].label}
                        </span>
                        <span className="font-mono text-xs text-gray-500">{r.redemption_id}</span>
                      </div>
                      <h3 className="mt-2 text-sm font-semibold text-spott-blue">
                        {r.units}× {r.space_name} · {r.months} mes(es)
                      </h3>
                      <p className="mt-1 text-xs text-gray-500">
                        {r.retailer_name} · {r.city} · solicitado el {r.created_at?.slice(0, 10)}
                      </p>
                      {r.staff_note && <p className="mt-2 text-xs text-gray-600">Nota Spott: {r.staff_note}</p>}
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1.5 text-lg font-bold text-spott-blue">
                        <Coins size={15} className="text-spott-yellow" /> {num(r.points_cost)}
                      </div>
                      <div className="text-xs text-gray-500">equivale a {money2(r.value_usd)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="redeem-modal">
          <DialogHeader>
            <DialogTitle className="text-spott-blue">Canjear con puntos Spott</DialogTitle>
            <DialogDescription>
              Elige cuántas unidades y meses quieres canjear. Un agente Spott confirmará la disponibilidad del
              espacio en la fecha que necesites.
            </DialogDescription>
          </DialogHeader>
          {modal && (
            <div className="space-y-4">
              <div className="rounded-lg bg-spott-light p-4">
                <h3 className="text-sm font-semibold text-spott-blue">{modal.name}</h3>
                <p className="mt-1 text-xs text-gray-500">
                  {modal.retailer_name} · {modal.city} · {modal.format}
                </p>
                <p className="mt-2 text-xs text-gray-600">
                  {num(modal.points_cost)} pts por unidad/mes · valor {money(modal.price_month)}
                </p>
              </div>
              {[["Unidades", "units"], ["Meses", "months"]].map(([label, key]) => (
                <div key={key} className="flex items-center justify-between">
                  <Label className="text-sm text-spott-blue">{label}</Label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setForm({ ...form, [key]: Math.max(1, form[key] - 1) })}
                      data-testid={`redeem-${key}-minus`} aria-label={`Menos ${label}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-spott-mid text-spott-blue">
                      <Minus size={14} />
                    </button>
                    <span className="w-6 text-center text-sm font-bold text-spott-blue"
                      data-testid={`redeem-${key}-value`}>{form[key]}</span>
                    <button onClick={() => setForm({ ...form, [key]: Math.min(24, form[key] + 1) })}
                      data-testid={`redeem-${key}-plus`} aria-label={`Más ${label}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-spott-yellow text-spott-blue">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <div className="space-y-2">
                <Label className="text-sm text-spott-blue">Comentario para tu agente (opcional)</Label>
                <Textarea rows={3} value={form.note} data-testid="redeem-note"
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Ej. Quisiera arrancar la primera semana de octubre" />
              </div>
              {(() => {
                const cost = modal.points_cost * form.units * form.months;
                const enough = balance >= cost;
                return (
                  <>
                    <div className="flex items-center justify-between rounded-lg bg-spott-blue p-4">
                      <span className="text-sm font-light text-spott-mid">Puntos a canjear</span>
                      <span className="text-xl font-bold text-spott-yellow" data-testid="redeem-cost">
                        {num(cost)} pts
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Saldo disponible: {num(balance)} pts · quedarían {num(Math.max(0, balance - cost))} pts
                    </p>
                    <button onClick={submitRedemption} disabled={busy || !enough} data-testid="redeem-submit"
                      className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue disabled:opacity-60">
                      {busy ? "Enviando…" : enough ? "Solicitar canje" : "Puntos insuficientes"}
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
